import React, { useState, useEffect, useRef } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Link, useRouter, usePathname } from "@/navigation";
import { useSearchParams, useParams } from "next/navigation";
import Image from "next/image";
import navbarIcon from "@/assets/images/navbar_icon.svg";
import {
  UserCircle,
  ChevronDown,
  LogOut,
  Settings,
  User,
  Building2,
  Calendar,
  Users,
  Home,
  CalendarClock,
  BarChart2,
  CreditCard,
  MoreHorizontal,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { loadVenues } from "./actions";
import { ATHLETE_ROLE, VENUE_OWNER_ROLE, STAFF_ROLE } from "@/constants/roles";
import { SUBSCRIPTION_UI_ENABLED } from "@/constants/featureFlags";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import { useTranslations } from "next-intl";
import Notifications from "./Notifications";
import SubscriptionStatus from "./SubscriptionStatus";
import { handleError } from "@/utils/errorHandler";
import BottomTabNavigation from "./BottomTabNavigation";
import VenueSwitcher from "./VenueSwitcher";
import { venueIdFromUrl } from "./venueScope";
import type { NavVenue } from "@/server-side/data/venue";

interface NavBarProps {
  showTimeDisplay?: boolean;
  timezone?: string;
  // Resolved on the server during the render. Without it the venue-scoped
  // items could only appear a round trip after hydration, which is what made
  // Staff pop into the row after everything else had settled. Null means the
  // server could not resolve them and the client still has to ask.
  initialVenues?: NavVenue[] | null;
}

// The width at which an item stops being worth its horizontal space and folds
// into the "More" menu, mirroring the bottom tab bar on mobile. "md" items are
// never collapsed.
type InlineFrom = "md" | "lg" | "xl";

interface MenuItem {
  key: string;
  label: string;
  href: string;
  icon: React.ElementType;
  show?: boolean;
  inlineFrom: InlineFrom;
  // Sections own more than one route, so equality against href is not enough.
  match?: (pathname: string) => boolean;
}

// Static class strings so Tailwind can see them.
const INLINE_DISPLAY: Record<InlineFrom, string> = {
  md: "flex",
  lg: "hidden lg:flex",
  xl: "hidden xl:flex",
};

// The mirror image: an item disappears from the overflow menu at exactly the
// width where it appears in the bar.
const OVERFLOW_DISPLAY: Record<InlineFrom, string> = {
  md: "hidden",
  lg: "flex lg:hidden",
  xl: "flex",
};

// Public pages that carry MarketingNavBar instead of this one.
const MARKETING_ROUTES: string[] = ["/", "/pricing", "/pre-registration"];

const ModernNavBar: React.FC<NavBarProps> = ({ initialVenues = null }) => {
  const t = useTranslations("Navigation");
  const { isAuthenticated, user, logout } = useAuth();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [fetchedVenues, setFetchedVenues] = useState<NavVenue[] | null>(null);
  const [isMoreOpen, setIsMoreOpen] = useState(false);
  const moreRef = useRef<HTMLDivElement>(null);
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const routeParams = useParams();

  // The layout resolves this while the page renders, so once it has answered
  // — including when the answer is "none" — it is both the freshest list and
  // the one the server's markup was built from. Refetching it here would only
  // repeat a call already made for this request.
  const venues = initialVenues ?? fetchedVenues ?? [];
  const firstVenueId = venues[0]?.id ?? null;

  // What the server could not answer: a session whose access token had expired
  // and was renewed by /api/status afterwards, a backend that was unreachable
  // during the render, and the standalone nav bar that /contact mounts outside
  // the layout.
  useEffect(() => {
    if (initialVenues) return;
    if (
      !isAuthenticated() ||
      !(
        user?.roles?.includes(VENUE_OWNER_ROLE) ||
        user?.roles?.includes(STAFF_ROLE)
      )
    ) {
      return;
    }

    let cancelled = false;

    const fetchVenues = async () => {
      try {
        const venuesData = await loadVenues();
        if (cancelled) return;
        setFetchedVenues(
          [...venuesData].sort((a, b) => a.name.localeCompare(b.name))
        );
      } catch (error) {
        handleError(error, { component: "ModernNavBar", action: "loadVenues" });
      }
    };

    fetchVenues();

    return () => {
      cancelled = true;
    };
  }, [user, isAuthenticated, initialVenues]);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    if (!isMoreOpen) return;
    const onClick = (e: MouseEvent) => {
      if (!moreRef.current?.contains(e.target as Node)) setIsMoreOpen(false);
    };
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && setIsMoreOpen(false);
    document.addEventListener("mousedown", onClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [isMoreOpen]);

  const handleLogout = async () => {
    await logout();
    setIsUserMenuOpen(false);
    router.push("/");
  };

  const handleSignUp = () => {
    localStorage.setItem("redirectAfterLogin", window.location.pathname);
    router.push("/sign-up");
  };

  // The venue you are currently looking at, so switching venue and then moving
  // between sections keeps you in that venue instead of snapping back to the
  // first one.
  const urlVenueId = venueIdFromUrl(pathname, searchParams, routeParams);
  const activeVenueId =
    (urlVenueId && venues.some((v) => v.id === urlVenueId)
      ? urlVenueId
      : null) ?? firstVenueId;

  const getMenuItems = (): MenuItem[] => {
    const isAthlete = user?.roles?.includes(ATHLETE_ROLE);
    const isVenueOwner = user?.roles?.includes(VENUE_OWNER_ROLE);
    const isStaff = user?.roles?.includes(STAFF_ROLE);

    // Don't show menu items if profile is not finalized
    if (!user?.irf) {
      return [];
    }

    if (isAthlete) {
      return [
        {
          key: "home",
          label: t("Menu.Home"),
          href: "/home",
          icon: Home,
          inlineFrom: "md",
        },
        {
          key: "browse",
          label: t("Menu.BrowseVenues"),
          href: "/browse",
          icon: Building2,
          inlineFrom: "md",
        },
      ];
    }

    if (isStaff) {
      return [
        {
          key: "reservations",
          label: t("Menu.Reservations"),
          href: activeVenueId
            ? `/venues/${activeVenueId}/reservations`
            : "/venues",
          icon: Calendar,
          show: true,
          inlineFrom: "md",
          match: (p) => p.includes("/reservations"),
        },
        {
          key: "recurring_reservations",
          label: t("Menu.RecurringReservations"),
          // Carries the venue so moving between sections does not silently
          // reset the scope back to the first venue.
          href: activeVenueId
            ? `/recurring-reservations?venueId=${activeVenueId}`
            : "/recurring-reservations",
          icon: CalendarClock,
          show: true,
          inlineFrom: "md",
          match: (p) => p === "/recurring-reservations",
        },
      ];
    }

    if (isVenueOwner) {
      return [
        {
          key: "reservations",
          label: t("Menu.Reservations"),
          href: activeVenueId
            ? `/venues/${activeVenueId}/reservations`
            : "/venues",
          icon: Calendar,
          show: true,
          inlineFrom: "md",
          match: (p) => p.includes("/reservations"),
        },
        {
          key: "recurring_reservations",
          label: t("Menu.RecurringReservations"),
          // Carries the venue so moving between sections does not silently
          // reset the scope back to the first venue.
          href: activeVenueId
            ? `/recurring-reservations?venueId=${activeVenueId}`
            : "/recurring-reservations",
          icon: CalendarClock,
          show: true,
          inlineFrom: "md",
          match: (p) => p === "/recurring-reservations",
        },
        {
          key: "venues",
          label: t("Menu.Venues"),
          // The venue switcher already picks which venue you are in, so this
          // goes straight to that venue's overview instead of via the list.
          href: activeVenueId ? `/venues/${activeVenueId}/overview` : "/venues",
          icon: Building2,
          inlineFrom: "lg",
          // The list is still reachable (no active venue, or by URL), so the
          // section stays lit there as well as on the overview it leads to.
          match: (p) => p === "/venues" || p.includes("/overview"),
        },
        {
          key: "staff",
          label: t("Menu.Staff"),
          // Like every other item, this falls back to the venue list rather
          // than waiting for a venue to be known. Hiding it until then made it
          // the one item that arrived after the row had already settled.
          href: activeVenueId ? `/venues/${activeVenueId}/staff` : "/venues",
          icon: Users,
          inlineFrom: "xl",
          match: (p) => p.includes("/staff"),
        },
        {
          key: "reports",
          label: t("Menu.Reports"),
          href: activeVenueId ? `/reports?venueId=${activeVenueId}` : "/reports",
          icon: BarChart2,
          show: true,
          inlineFrom: "xl",
          match: (p) => p === "/reports",
        },
      ];
    }

    return [];
  };

  const menuItems = getMenuItems().filter((item) => item.show !== false);
  const isItemActive = (item: MenuItem) =>
    item.match ? item.match(pathname) : pathname === item.href;

  // Everything that can fold away, and the width at which the last of them
  // comes back inline (which is when the "More" button itself goes away).
  const overflowItems = menuItems.filter((item) => item.inlineFrom !== "md");
  const moreHiddenFrom = overflowItems.some((item) => item.inlineFrom === "xl")
    ? "xl:hidden"
    : "lg:hidden";
  const activeOverflowItems = overflowItems.filter(isItemActive);
  // If the only active overflow item is one that goes inline at lg, the button
  // must stop looking active there too, or the page reads as selected twice.
  const moreActiveReset =
    activeOverflowItems.length > 0 &&
    activeOverflowItems.every((item) => item.inlineFrom === "lg")
      ? "lg:text-gray-300 lg:bg-transparent"
      : "";

  // Marketing routes render their own public nav (MarketingNavBar), so the
  // in-app chrome stands down there instead of stacking a second bar on top.
  const isMarketingPage = MARKETING_ROUTES.includes(pathname);
  const isOwner = !!user?.roles?.includes(VENUE_OWNER_ROLE);

  if (isMarketingPage) return null;

  const userMenuItems = [
    {
      key: "profile",
      label: t("UserMenu.Profile"),
      icon: User,
      onClick: () => router.push("/profile"),
    },
    // Venue owners get a shortcut to manage their (first) venue's subscription.
    ...(SUBSCRIPTION_UI_ENABLED && user?.roles?.includes(VENUE_OWNER_ROLE) && activeVenueId
      ? [
          {
            key: "subscription",
            label: t("Menu.Subscription"),
            icon: CreditCard,
            onClick: () =>
              router.push(`/venues/${activeVenueId}/subscription` as any),
          },
        ]
      : []),
    {
      key: "logout",
      label: t("UserMenu.Logout"),
      icon: LogOut,
      onClick: handleLogout,
      className: "text-red-400 hover:text-red-300",
    },
  ];

  return (
    <>
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled
            ? "bg-gray-900/95 backdrop-blur-md shadow-lg"
            : "bg-gray-900/30 backdrop-blur-sm"
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center gap-2 h-16">
            {/* Logo and the venue this session is scoped to */}
            <div className="flex items-center gap-2 shrink-0">
              {/* shrink-0 matters: the mark is a wordmark, so without it the
                  logo is the only item with no minimum width and absorbs
                  every pixel the row is short. */}
              <Link
                href="/"
                className="flex items-center shrink-0 transition-transform duration-300 hover:scale-105"
              >
                <Image
                  src={navbarIcon}
                  alt="Atlivo"
                  width={32}
                  height={32}
                  // The wordmark is ~4:1, so 32px tall costs ~131px of a
                  // phone's 360. Smaller on mobile to leave room for the
                  // venue switcher beside it.
                  className="h-6 md:h-8 w-auto max-w-none"
                  priority
                />
              </Link>

              {/* Shown at every width: the scope control is the same one
                  on a phone as on a desktop, it just opens a bottom sheet
                  down there. */}
              {isAuthenticated() && user?.irf && (
                <VenueSwitcher venues={venues} activeVenueId={activeVenueId} />
              )}
            </div>

            {/* Desktop Navigation. Items drop into "More" as the row runs
                out of width, the same priority ladder the bottom tab bar
                uses on mobile. */}
            <div className="hidden md:flex items-center gap-0.5 lg:gap-1 xl:gap-2 min-w-0">
              {menuItems.map((item) => {
                const Icon = item.icon;

                return (
                  <Link
                    key={item.key}
                    href={item.href as any}
                    className={`${INLINE_DISPLAY[item.inlineFrom]} items-center gap-1.5 whitespace-nowrap text-sm px-2 xl:px-3 py-1.5 rounded-lg transition-colors duration-200 ${
                      isItemActive(item)
                        ? "text-white bg-gray-700/30"
                        : "text-gray-300 hover:text-white hover:bg-gray-700/50"
                    }`}
                  >
                    <Icon className="w-4 h-4 shrink-0" />
                    <span>{item.label}</span>
                  </Link>
                );
              })}

              {overflowItems.length > 0 && (
                <div className={`relative ${moreHiddenFrom}`} ref={moreRef}>
                  <button
                    onClick={() => setIsMoreOpen(!isMoreOpen)}
                    aria-label={t("Menu.More")}
                    aria-expanded={isMoreOpen}
                    className={`flex items-center gap-1.5 whitespace-nowrap text-sm px-2 py-1.5 rounded-lg transition-colors duration-200 ${
                      activeOverflowItems.length > 0
                        ? `text-white bg-gray-700/30 ${moreActiveReset}`
                        : "text-gray-300 hover:text-white hover:bg-gray-700/50"
                    }`}
                  >
                    <MoreHorizontal className="w-4 h-4 shrink-0" />
                    {/* Below lg the label costs more than it explains. */}
                    <span className="hidden lg:inline">{t("Menu.More")}</span>
                    <ChevronDown
                      className={`w-3.5 h-3.5 shrink-0 transition-transform duration-200 ${
                        isMoreOpen ? "rotate-180" : ""
                      }`}
                    />
                  </button>

                  <AnimatePresence>
                    {isMoreOpen && (
                      <motion.div
                        initial={{ opacity: 0, y: 8 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: 8 }}
                        transition={{ duration: 0.15 }}
                        className="absolute left-0 mt-2 w-56 rounded-xl bg-gray-800 border border-gray-700 shadow-lg py-1 z-50"
                      >
                        {overflowItems.map((item) => {
                          const Icon = item.icon;

                          return (
                            <Link
                              key={item.key}
                              href={item.href as any}
                              onClick={() => setIsMoreOpen(false)}
                              className={`${OVERFLOW_DISPLAY[item.inlineFrom]} items-center gap-2 px-4 py-2 text-sm transition-colors hover:bg-gray-700/50 hover:text-white ${
                                isItemActive(item)
                                  ? "text-white"
                                  : "text-gray-300"
                              }`}
                            >
                              <Icon className="w-4 h-4 shrink-0" />
                              <span className="truncate">{item.label}</span>
                            </Link>
                          );
                        })}
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              )}
            </div>

            {/* Right side actions, one cluster at every width. These used to be
                two components picked apart by a JS media query, which meant
                none of it could render until a tick after hydration however
                fast the data arrived. The breakpoint differences are small
                enough to say in CSS, and saying them there means the bar is
                complete in the server's markup. */}
            <div className="flex items-center gap-1.5 md:gap-1 xl:gap-3 shrink-0">
              {/* Billing health sits next to notifications: both are things
                  the owner needs to notice without going looking. */}
              {SUBSCRIPTION_UI_ENABLED && isAuthenticated() && isOwner && user?.irf && (
                <SubscriptionStatus venueId={activeVenueId} />
              )}

              {isAuthenticated() && (user?.roles?.includes(VENUE_OWNER_ROLE) || user?.roles?.includes(STAFF_ROLE)) && user?.irf && (
                <Notifications />
              )}

              <div className="relative">
                <button
                  onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                  className="flex items-center space-x-1 md:space-x-2 text-sm text-gray-300 hover:text-white focus:outline-none transition-all duration-300 hover:bg-gray-800/50 p-2 rounded-lg"
                >
                  <UserCircle className="w-6 h-6" />
                  <ChevronDown
                    className={`w-3 h-3 md:w-4 md:h-4 transition-transform duration-200 ${
                      isUserMenuOpen ? "rotate-180" : ""
                    }`}
                  />
                </button>

                <AnimatePresence>
                  {isUserMenuOpen && (
                    <motion.div
                      initial={{ opacity: 0, y: 10, scale: 0.95 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      exit={{ opacity: 0, y: 10, scale: 0.95 }}
                      className="absolute right-0 mt-2 w-48 rounded-xl bg-gray-800/95 backdrop-blur-md border border-gray-700 shadow-xl py-1 z-50"
                    >
                      {isAuthenticated() ? (
                        <>
                          <div className="px-4 py-2 border-b border-gray-700">
                            <p className="text-sm text-gray-400">
                              {t("UserMenu.SignedInAs")}
                            </p>
                            <p className="text-sm font-medium text-white truncate">
                              {user?.given_name}
                            </p>
                          </div>
                          {userMenuItems.map((item) => {
                            const Icon = item.icon;
                            return (
                              <button
                                key={item.key}
                                onClick={() => {
                                  item.onClick();
                                  setIsUserMenuOpen(false);
                                }}
                                className={`w-full px-4 py-2 text-sm text-gray-300 hover:bg-gray-700/50 flex items-center space-x-2 transition-all duration-300 hover:translate-x-1 ${
                                  item.className || ""
                                }`}
                              >
                                <Icon className="w-4 h-4" />
                                <span>{item.label}</span>
                              </button>
                            );
                          })}
                        </>
                      ) : (
                        <button
                          onClick={handleSignUp}
                          className="w-full px-4 py-2 text-sm text-blue-400 hover:bg-gray-700/50 flex items-center space-x-2 transition-all duration-300 hover:translate-x-1"
                        >
                          <UserCircle className="w-4 h-4" />
                          <span>{t("UserMenu.SignUp")}</span>
                        </button>
                      )}
                      <div className="border-t border-gray-700 mt-1 pt-1">
                        <LanguageSwitcher isMobile={true} onLanguageChange={() => setIsUserMenuOpen(false)} />
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </div>
          </div>
        </div>
      </nav>

      {/* Bottom Tab Navigation for Mobile */}
      {isAuthenticated() && (
        <BottomTabNavigation activeVenueId={activeVenueId} />
      )}
    </>
  );
};

export default ModernNavBar;